// Branding assets configuration
// Easily replaceable constants for all branding assets

export const BRANDING_ASSETS = {
  // Favicon assets
  FAVICON_SVG: "/assets/branding/favicon.svg",
  FAVICON_32: "/assets/branding/favicon-32.png", 
  FAVICON_48: "/assets/branding/favicon-48.png",
  APPLE_TOUCH_ICON: "/assets/branding/apple-touch-icon-180.png",
  ICON_512: "/assets/branding/icon-512.png",
  
  // Social preview images
  SOCIAL_PREVIEW_DEFAULT: "/social-preview.png", // New default image
  OG_ES: "/assets/branding/og-es.png",
  OG_FR: "/assets/branding/og-fr.png", 
  OG_DEFAULT: "/assets/branding/og-default.png",
  
  // Manifest
  MANIFEST: "/manifest.webmanifest"
};

// 🎨 Unified Color Palette for DCA Club Ecosystem
export const BRAND_COLORS = {
  // Backgrounds
  BACKGROUND_PRIMARY: "#07121E",    // Main background (darker, more professional)
  BACKGROUND_CARDS: "#0C1A2A",      // Cards and internal sections (warmer)
  BACKGROUND_LEGACY: "#040816",     // Keep for transition compatibility
  
  // Text colors
  TEXT_PRIMARY: "rgba(255, 255, 255, 0.95)",  // Main text (95% white)
  TEXT_SECONDARY: "#9EB4C0",                   // Subtitles and descriptions
  TEXT_MUTED: "rgba(255, 255, 255, 0.7)",     // Muted text
  
  // Brand accents
  ACCENT_PRIMARY: "#00F4FF",        // Cyan blue (links, hover states)
  ACCENT_SECONDARY: "#12B7FF",      // Secondary blue
  ACCENT_TERTIARY: "#0677aa",       // Tertiary blue (keeping existing)
  
  // Interactive elements
  BORDER_SUBTLE: "rgba(0, 244, 255, 0.05)",   // Subtle borders and shadows
  BORDER_DEFAULT: "rgba(18, 183, 255, 0.3)",  // Default borders
  HOVER_BACKGROUND: "rgba(0, 244, 255, 0.08)", // Hover states
  
  // Gradients
  GRADIENT_PRIMARY: "linear-gradient(135deg, #00F4FF 0%, #12B7FF 100%)",
  GRADIENT_BACKGROUND: "linear-gradient(180deg, #07121E 0%, #0C1A2A 100%)",
  
  // Status colors
  SUCCESS: "#10B981",
  WARNING: "#F59E0B", 
  ERROR: "#EF4444",
  
  // Legacy compatibility (will be phased out)
  PRIMARY: "#00f4ff",
  SECONDARY: "#12b7ff",
  BACKGROUND: "#040816"
};

// Meta tag utility functions
export const updateMetaTags = (lang, t) => {
  const currentUrl = window.location.href;
  
  // Use new default titles and descriptions
  const defaultTitle = "DCA Visión 360° · DCA Club";
  const defaultDescription = "Herramienta educativa para aprender a modelar un portafolio digital.";
  
  // Get appropriate social image based on language with fallback to new default
  const socialImageUrl = getSocialImageUrl(lang);
  const socialImageAlt = getSocialImageAlt(lang, t);
  
  // Update document title - use default or translated version
  document.title = t('metaTitle') || defaultTitle;
  
  // Update or create meta description - use default or translated version
  updateMetaTag('name', 'description', t('metaDesc') || defaultDescription);
  
  // Update Open Graph tags
  updateMetaTag('property', 'og:type', 'website');
  updateMetaTag('property', 'og:site_name', 'DCA Club');
  updateMetaTag('property', 'og:title', t('metaTitle') || defaultTitle);
  updateMetaTag('property', 'og:description', t('metaDesc') || defaultDescription);
  updateMetaTag('property', 'og:image', socialImageUrl);
  updateMetaTag('property', 'og:image:width', '1200');
  updateMetaTag('property', 'og:image:height', '630');
  updateMetaTag('property', 'og:image:alt', socialImageAlt);
  updateMetaTag('property', 'og:url', currentUrl);
  
  // Update Twitter Card tags
  updateMetaTag('name', 'twitter:card', 'summary_large_image');
  updateMetaTag('name', 'twitter:site', '@TheDCAClub');
  updateMetaTag('name', 'twitter:creator', '@TheDCAClub');
  updateMetaTag('name', 'twitter:title', t('metaTitle') || defaultTitle);
  updateMetaTag('name', 'twitter:description', t('metaDesc') || defaultDescription);
  updateMetaTag('name', 'twitter:image', socialImageUrl);
  updateMetaTag('name', 'twitter:image:alt', socialImageAlt);
};

// Get social image URL with language-specific overrides and fallback
const getSocialImageUrl = (lang) => {
  const baseUrl = window.location.origin;
  
  // Cache-busting version parameter (update when images change)
  const version = '20250911'; // Format: YYYYMMDD
  
  // Check if language-specific image exists, otherwise use default
  let imagePath;
  
  if (lang === 'es') {
    imagePath = BRANDING_ASSETS.OG_ES;
  } else if (lang === 'fr') {
    imagePath = BRANDING_ASSETS.OG_FR;
  } else {
    imagePath = BRANDING_ASSETS.SOCIAL_PREVIEW_DEFAULT;
  }
  
  // For production, use the configured assets base URL
  const assetsBaseUrl = process.env.REACT_APP_ASSETS_BASE_URL;
  if (assetsBaseUrl && (baseUrl.includes('sim.thedcaclub.com') || baseUrl.includes('planner.thedcaclub.com'))) {
    return `${assetsBaseUrl}${imagePath}?v=${version}`;
  }
  
  // For other deployments or local development, use current origin
  return `${baseUrl}${imagePath}?v=${version}`;
};

// Get appropriate alt text for social images
const getSocialImageAlt = (lang, t) => {
  const defaultAltEs = "DCA Visión 360° - Herramienta educativa para modelar portafolios digitales";
  const defaultAltFr = "DCA Vision 360° - Outil éducatif pour modéliser des portefeuilles digitaux";
  const defaultAltEn = "DCA Vision 360° - Educational tool for digital portfolio modeling";
  
  // Use translated alt text if available, otherwise use defaults
  if (lang === 'es') {
    return t('socialImageAlt') || defaultAltEs;
  } else if (lang === 'fr') {
    return t('socialImageAlt') || defaultAltFr;
  } else {
    return t('socialImageAlt') || defaultAltEn;
  }
};

// Helper function to update or create meta tags
const updateMetaTag = (attributeName, attributeValue, content) => {
  let tag = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attributeName, attributeValue);
    document.head.appendChild(tag);
  }
  
  tag.setAttribute('content', content);
};

// Helper function to get absolute URL
const getAbsoluteUrl = (path) => {
  return new URL(path, window.location.origin).toString();
};

// Initialize favicon and static meta tags
export const initializeFavicons = () => {
  // Remove existing favicon and manifest links to avoid conflicts
  const existingLinks = document.querySelectorAll('link[rel*="icon"], link[rel="manifest"]');
  existingLinks.forEach(link => link.remove());
  
  // Add favicon links
  addFaviconLink('icon', BRANDING_ASSETS.FAVICON_SVG, 'image/svg+xml');
  addFaviconLink('alternate icon', BRANDING_ASSETS.FAVICON_32, 'image/png', '32x32');
  addFaviconLink('alternate icon', BRANDING_ASSETS.FAVICON_48, 'image/png', '48x48');
  addFaviconLink('apple-touch-icon', BRANDING_ASSETS.APPLE_TOUCH_ICON, 'image/png', '180x180');
  addFaviconLink('manifest', BRANDING_ASSETS.MANIFEST);
  
  // Add preload for SVG favicon
  addFaviconLink('preload', BRANDING_ASSETS.FAVICON_SVG, 'image/svg+xml', null, 'image');
  
  // Add theme color and color scheme
  updateMetaTag('name', 'theme-color', BRAND_COLORS.BACKGROUND);
  updateMetaTag('name', 'color-scheme', 'dark light');
};

// Helper function to add favicon links
const addFaviconLink = (rel, href, type = null, sizes = null, as = null) => {
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  
  if (type) link.type = type;
  if (sizes) link.sizes = sizes;
  if (as) link.as = as;
  
  document.head.appendChild(link);
};